import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getClient, createClient, updateClient, getWorkers, getInvoices, getServiceOrders, getPayments } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Building2, User, Plus, Users, FileText, ClipboardList, Save, FolderOpen, Layers, Wallet, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getInvoiceStatusClass, getOrderStatusClass, getExpiryStatusClass } from '@/lib/utils';
import DocumentsTab from '@/components/DocumentsTab';
import CustomFieldsRenderer from '@/components/CustomFieldsRenderer';
import WalletSection from '@/components/WalletSection';

const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'UAQ', 'Ras Al Khaimah', 'Fujairah'];
const paymentTerms = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 60'];

// Payment method colors
const paymentMethodColors = {
  'Wallet': 'bg-primary/10 text-primary border-primary/30',
  'Card': 'bg-blue-50 text-blue-700 border-blue-200',
  'Bank Transfer': 'bg-green-50 text-green-700 border-green-200',
  'Cheque': 'bg-amber-50 text-amber-700 border-amber-200',
  'Cash': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Online': 'bg-purple-50 text-purple-700 border-purple-200'
};

// Payment method icons
const PaymentMethodIcon = ({ method }) => {
  switch (method) {
    case 'Wallet':
      return <Wallet className="w-4 h-4 text-primary" />;
    case 'Card':
      return <CreditCard className="w-4 h-4 text-blue-500" />;
    case 'Bank Transfer':
      return <Building2 className="w-4 h-4 text-green-600" />;
    default:
      return <Banknote className="w-4 h-4 text-emerald-500" />;
  }
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const isEdit = window.location.pathname.endsWith('/edit');

  const [client, setClient] = useState({
    client_type: 'Company',
    trade_name: '',
    full_name: '',
    emirate: 'Dubai',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    license_no: '',
    trn: '',
    credit_limit: 0,
    payment_terms: 'Due on Receipt',
    notes: '',
    custom_fields: {}
  });
  const [workers, setWorkers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      loadClient();
    }
  }, [id, isNew]);

  const loadClient = async () => {
    try {
      const [clientRes, workersRes, invoicesRes, ordersRes, paymentsRes] = await Promise.all([
        getClient(id),
        getWorkers({ client_id: id }),
        getInvoices({ client_id: id }),
        getServiceOrders({ client_id: id }),
        getPayments({ client_id: id }).catch(() => ({ data: { items: [] } }))
      ]);
      setClient(clientRes.data);
      setWorkers(workersRes.data.items || []);
      setInvoices(invoicesRes.data.items || []);
      setOrders(ordersRes.data.items || []);
      setPayments(paymentsRes.data.items || []);
    } catch (error) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await createClient(client);
        toast.success('Client created');
        navigate(`/clients/${res.data.id}`);
      } else {
        await updateClient(id, client);
        toast.success('Client updated');
        navigate(`/clients/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setClient((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  // Form view for new/edit
  if (isNew || isEdit) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{isNew ? 'New Client' : 'Edit Client'}</h1>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Client Type</Label>
                  <Select 
                    value={client.client_type} 
                    onValueChange={(v) => handleChange('client_type', v)}
                    disabled={!isNew}
                  >
                    <SelectTrigger data-testid="client-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Company">Company</SelectItem>
                      <SelectItem value="Individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {client.client_type === 'Company' ? (
                  <div>
                    <Label>Trade Name *</Label>
                    <Input
                      value={client.trade_name || ''}
                      onChange={(e) => handleChange('trade_name', e.target.value)}
                      placeholder="Company name"
                      data-testid="client-trade-name"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={client.full_name || ''}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      placeholder="Individual name"
                      data-testid="client-full-name"
                    />
                  </div>
                )}

                <div>
                  <Label>Emirate *</Label>
                  <Select value={client.emirate} onValueChange={(v) => handleChange('emirate', v)}>
                    <SelectTrigger data-testid="client-emirate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emirates.map((e) => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>License No</Label>
                  <Input
                    value={client.license_no || ''}
                    onChange={(e) => handleChange('license_no', e.target.value)}
                    placeholder="Trade license number"
                  />
                </div>

                <div>
                  <Label>TRN (Tax Reg. No)</Label>
                  <Input
                    value={client.trn || ''}
                    onChange={(e) => handleChange('trn', e.target.value)}
                    placeholder="100000000000003"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={client.contact_person || ''}
                    onChange={(e) => handleChange('contact_person', e.target.value)}
                    placeholder="Contact name"
                    data-testid="client-contact-person"
                  />
                </div>

                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={client.contact_email || ''}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="email@example.com"
                    data-testid="client-email"
                  />
                </div>

                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={client.contact_phone || ''}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="+971 50 XXX XXXX"
                    data-testid="client-phone"
                  />
                </div>

                <div>
                  <Label>Credit Limit (AED)</Label>
                  <Input
                    type="number"
                    value={client.credit_limit || 0}
                    onChange={(e) => handleChange('credit_limit', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div>
                  <Label>Payment Terms</Label>
                  <Select value={client.payment_terms} onValueChange={(v) => handleChange('payment_terms', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTerms.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={client.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Full address"
                  rows={2}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={client.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Internal notes"
                  rows={3}
                />
              </div>

              {/* Custom Fields Section */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center gap-2 mb-4">
                  <Layers className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm">Additional Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CustomFieldsRenderer
                    entityType="Client"
                    values={client.custom_fields || {}}
                    onChange={(fieldName, value) => {
                      setClient(prev => ({
                        ...prev,
                        custom_fields: {
                          ...prev.custom_fields,
                          [fieldName]: value
                        }
                      }));
                    }}
                    mode="form"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => navigate(isNew ? '/clients' : `/clients/${id}`)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Client'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detail view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              {client.client_type === 'Company' ? (
                <Building2 className="w-6 h-6 text-primary" />
              ) : (
                <User className="w-6 h-6 text-primary" />
              )}
              <h1 className="text-2xl font-bold">{client.display_name}</h1>
              <Badge>{client.client_type}</Badge>
              <Badge variant={client.status === 'Active' ? 'default' : 'secondary'}>
                {client.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{client.client_code} • {client.emirate}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/edit`)}>
            Edit
          </Button>
          <Button onClick={() => navigate(`/service-orders/new?client_id=${id}`)}>
            <Plus className="w-4 h-4 mr-2" />
            New Order
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Contact Info</h3>
            <p className="font-medium">{client.contact_person || '-'}</p>
            <p className="text-sm text-muted-foreground">{client.contact_email}</p>
            <p className="text-sm text-muted-foreground">{client.contact_phone}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Outstanding Balance</h3>
            <p className="text-2xl font-mono font-semibold text-red-500">
              {formatCurrency(client.outstanding_balance)}
            </p>
            <p className="text-sm mt-2">Credit Limit: {formatCurrency(client.credit_limit)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">Wallet Balance</h3>
            </div>
            <p className="text-2xl font-mono font-semibold text-primary" data-testid="client-wallet-balance">
              {formatCurrency(client.wallet_balance || 0)}
            </p>
            <p className="text-sm mt-2 text-muted-foreground">Available for payments</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">License Info</h3>
            <p className="font-mono">{client.license_no || '-'}</p>
            <p className="text-sm text-muted-foreground">TRN: {client.trn || '-'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Custom Fields Display */}
      {client.custom_fields && Object.keys(client.custom_fields).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Additional Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CustomFieldsRenderer
              entityType="Client"
              values={client.custom_fields}
              mode="display"
              readOnly
            />
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="wallet" className="w-full">
        <TabsList>
          <TabsTrigger value="wallet" className="flex items-center gap-1">
            <Wallet className="w-4 h-4" />
            Wallet
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-1">
            <CreditCard className="w-4 h-4" />
            Payments ({payments.length})
          </TabsTrigger>
          <TabsTrigger value="workers" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Workers ({workers.length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-1">
            <FolderOpen className="w-4 h-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-1">
            <ClipboardList className="w-4 h-4" />
            Orders ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Invoices ({invoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="wallet" className="mt-4">
          <WalletSection clientId={id} clientName={client.display_name} />
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Payment History</CardTitle>
              <Button size="sm" onClick={() => navigate(`/payments`)}>
                <Plus className="w-4 h-4 mr-1" /> Record Payment
              </Button>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No payments recorded yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Date</th>
                        <th className="text-left py-2">Receipt #</th>
                        <th className="text-left py-2">Method</th>
                        <th className="text-left py-2">Reference</th>
                        <th className="text-right py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-b border-border/50">
                          <td className="py-2 text-muted-foreground">{formatDate(p.payment_date)}</td>
                          <td className="py-2 font-mono">
                            {p.payment_number || p.receipt_number}
                            {p.is_split_payment && (
                              <Badge variant="outline" className="ml-2 text-xs">Split</Badge>
                            )}
                          </td>
                          <td className="py-2">
                            <div className="flex items-center gap-2">
                              <PaymentMethodIcon method={p.payment_method} />
                              <Badge 
                                variant="outline" 
                                className={paymentMethodColors[p.payment_method] || 'bg-gray-50 text-gray-700'}
                              >
                                {p.payment_method}
                              </Badge>
                            </div>
                          </td>
                          <td className="py-2 text-muted-foreground">{p.reference_number || '-'}</td>
                          <td className="py-2 text-right font-mono font-semibold text-green-600">
                            {formatCurrency(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Payments</span>
                    <span className="font-mono font-semibold text-green-600">
                      {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workers" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Workers</CardTitle>
              <Button size="sm" onClick={() => navigate(`/workers/new?client_id=${id}`)}>
                <Plus className="w-4 h-4 mr-1" /> Add Worker
              </Button>
            </CardHeader>
            <CardContent>
              {workers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No workers added yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Name</th>
                        <th className="text-left py-2">Job Title</th>
                        <th className="text-left py-2">Visa Expiry</th>
                        <th className="text-left py-2">EID Expiry</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workers.map((w) => (
                        <tr key={w.id} className="border-b border-border/50">
                          <td className="py-2">
                            <Link to={`/workers/${w.id}`} className="font-medium hover:text-primary">
                              {w.full_name}
                            </Link>
                          </td>
                          <td className="py-2 text-muted-foreground">{w.job_title || '-'}</td>
                          <td className="py-2">
                            {w.visa_expiry && (
                              <Badge className={getExpiryStatusClass(w.visa_expiry_days)}>
                                {formatDate(w.visa_expiry)}
                              </Badge>
                            )}
                          </td>
                          <td className="py-2">
                            {w.emirates_id_expiry && (
                              <Badge className={getExpiryStatusClass(w.emirates_id_expiry_days)}>
                                {formatDate(w.emirates_id_expiry)}
                              </Badge>
                            )}
                          </td>
                          <td className="py-2">
                            <Badge variant="secondary">{w.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsTab 
            entityType="Client" 
            entityId={id} 
            entityName={client.display_name}
          />
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {orders.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No service orders yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Order #</th>
                        <th className="text-left py-2">Service</th>
                        <th className="text-left py-2">Beneficiary</th>
                        <th className="text-right py-2">Amount</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id} className="border-b border-border/50">
                          <td className="py-2 font-mono">
                            <Link to={`/service-orders/${o.id}`} className="hover:text-primary">
                              {o.order_number}
                            </Link>
                          </td>
                          <td className="py-2">{o.service_name}</td>
                          <td className="py-2 text-muted-foreground">{o.beneficiary_name || '-'}</td>
                          <td className="py-2 text-right font-mono">{formatCurrency(o.grand_total)}</td>
                          <td className="py-2">
                            <Badge className={getOrderStatusClass(o.status)}>{o.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No invoices yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Invoice #</th>
                        <th className="text-left py-2">Date</th>
                        <th className="text-right py-2">Total</th>
                        <th className="text-right py-2">Balance</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-border/50">
                          <td className="py-2 font-mono">
                            <Link to={`/invoices/${inv.id}`} className="hover:text-primary">
                              {inv.invoice_number}
                            </Link>
                          </td>
                          <td className="py-2 text-muted-foreground">{formatDate(inv.invoice_date)}</td>
                          <td className="py-2 text-right font-mono">{formatCurrency(inv.total_amount)}</td>
                          <td className="py-2 text-right font-mono">{formatCurrency(inv.balance_due)}</td>
                          <td className="py-2">
                            <Badge className={getInvoiceStatusClass(inv.status)}>{inv.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
